import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronUp, ChevronDown, ArrowLeftRight, Loader2 } from 'lucide-react';
import PhotoUpload from './PhotoUpload';

/**
 * MainHistoryEditor
 * -----------------
 * Same UX as HistoryEditor but backed by page_content.history (main site) so
 * the Jurisdictional /history page pulls its own timeline separate from the
 * Women's Ministry one.
 */

export default function MainHistoryEditor() {
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get('/site/page-content/history'); setC(r.data || {}); }
    catch { /* new */ }
  };
  useEffect(() => { load(); }, []);

  const timer = useRef(null);
  const pending = useRef(null);
  const persist = (patch, { immediate = false } = {}) => {
    pending.current = patch;
    if (timer.current) clearTimeout(timer.current);
    const flush = async () => {
      const draft = pending.current; pending.current = null;
      if (!draft) return;
      setBusy(true);
      try { await api.patch('/site/page-content/history', draft); await load(); toast.success('Saved · live'); }
      catch { toast.error('Save failed'); } finally { setBusy(false); }
    };
    if (immediate) return flush();
    timer.current = setTimeout(flush, 700);
  };
  const setField = (k, v) => setC(p => ({ ...p, [k]: v }));

  const entries = (c.history_entries || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const saveHero = () => persist({
    hero_heading: c.hero_heading, hero_blurb: c.hero_blurb,
    hero_image: c.hero_image, hero_video_url: c.hero_video_url,
  }, { immediate: true });

  const addEntry = () => {
    const order = entries.length ? Math.max(...entries.map(e => e.order ?? 0)) + 1 : 0;
    const next = [...entries, {
      id: `mhe_${Date.now()}`, order,
      year: '1978', date: '', title: 'New Moment', text: 'Describe what happened.',
      image: '', side: entries.length % 2 === 0 ? 'left' : 'right',
    }];
    persist({ history_entries: next }, { immediate: true });
  };
  const updateEntry = (id, patch) => persist({ history_entries: entries.map(e => e.id === id ? { ...e, ...patch } : e) });
  const removeEntry = (id) => {
    if (!window.confirm('Remove this history entry?')) return;
    persist({ history_entries: entries.filter(e => e.id !== id) }, { immediate: true });
  };
  const move = (id, delta) => {
    const idx = entries.findIndex(e => e.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= entries.length) return;
    const next = [...entries];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    persist({ history_entries: next.map((e, i) => ({ ...e, order: i })) }, { immediate: true });
  };
  const flipSide = (e) => updateEntry(e.id, { side: e.side === 'right' ? 'left' : 'right' });

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-lg p-6 space-y-4">
        <h4 className="font-serif text-lg text-royal font-bold">Cover (image or video)</h4>
        <PhotoUpload label="Cover image" value={c.hero_image} onChange={(v) => setField('hero_image', v)} category="pages" />
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Cover VIDEO (mp4 URL — overrides image if set)</div>
          <input value={c.hero_video_url || ''} onChange={(e) => setField('hero_video_url', e.target.value)}
            data-testid="main-history-hero-video-url"
            placeholder="https://.../video.mp4" className="w-full px-3 py-2 border rounded font-mono text-xs" />
        </div>
        <PhotoUpload label="Or upload a cover video (mp4)" value={c.hero_video_url} onChange={(v) => setField('hero_video_url', v)} category="pages" accept="video/*" />
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Hero heading</div>
          <input value={c.hero_heading || ''} onChange={(e) => setField('hero_heading', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </div>
        <div>
          <div className="text-sm font-semibold text-royal mb-1">Hero blurb</div>
          <textarea rows="2" value={c.hero_blurb || ''} onChange={(e) => setField('hero_blurb', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </div>
        <div><button onClick={saveHero} disabled={busy} data-testid="main-history-hero-save" className="btn-royal">{busy ? 'Saving…' : 'Save Cover'}</button></div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h4 className="font-serif text-lg text-royal font-bold">Timeline Entries ({entries.length})</h4>
          <p className="text-xs text-charcoal-muted mt-1">Each entry appears in the top timeline strip and as a paragraph with alternating photo placement. Visitors can click any year to jump to that entry.</p>
        </div>
        <button onClick={addEntry} disabled={busy} data-testid="main-history-add-entry"
          className="btn-royal text-sm py-2 px-4 flex items-center gap-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Entry
        </button>
      </div>

      <div className="space-y-4">
        {entries.length === 0 && (
          <div className="bg-white border rounded-lg p-8 text-center text-sm text-charcoal-muted">
            No entries yet. Click <strong>Add Entry</strong> to add the first milestone of the jurisdiction.
          </div>
        )}
        {entries.map((e, i) => (
          <div key={e.id} data-testid={`main-history-entry-${e.id}`} className="bg-white border rounded-lg p-4 grid md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <PhotoUpload label={`Photo (${e.side === 'right' ? 'right' : 'left'} side)`} value={e.image} onChange={(v) => updateEntry(e.id, { image: v })} category="pages" />
              <button onClick={() => flipSide(e)} data-testid={`main-history-flip-${e.id}`}
                className="mt-2 text-xs px-3 py-1.5 border border-royal text-royal rounded-full hover:bg-royal hover:text-white transition flex items-center gap-1">
                <ArrowLeftRight className="w-3 h-3" /> Switch to {e.side === 'right' ? 'left' : 'right'} side
              </button>
            </div>
            <div className="md:col-span-8 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input value={e.year || ''} onChange={(ev) => updateEntry(e.id, { year: ev.target.value })}
                  data-testid={`main-history-year-${e.id}`}
                  placeholder="Year (e.g. 1978)" className="px-3 py-2 border rounded font-serif text-lg font-bold text-royal" />
                <input value={e.date || ''} onChange={(ev) => updateEntry(e.id, { date: ev.target.value })}
                  placeholder="Date / Season" className="px-3 py-2 border rounded text-sm" />
              </div>
              <input value={e.title || ''} onChange={(ev) => updateEntry(e.id, { title: ev.target.value })}
                data-testid={`main-history-title-${e.id}`}
                placeholder="Entry title" className="w-full px-3 py-2 border rounded font-semibold" />
              <textarea rows="5" value={e.text || ''} onChange={(ev) => updateEntry(e.id, { text: ev.target.value })}
                data-testid={`main-history-text-${e.id}`}
                placeholder="Paragraph text" className="w-full px-3 py-2 border rounded" />
              <div className="flex items-center gap-1 pt-1">
                <button onClick={() => move(e.id, -1)} disabled={i === 0 || busy} title="Move up" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(e.id, 1)} disabled={i === entries.length - 1 || busy} title="Move down" className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <select value={e.side || 'left'} onChange={(ev) => persist({ history_entries: entries.map(x => x.id === e.id ? { ...x, side: ev.target.value } : x) }, { immediate: true })} className="text-xs px-2 py-1 border rounded">
                  <option value="left">Photo left</option>
                  <option value="right">Photo right</option>
                </select>
                <button onClick={() => removeEntry(e.id)} className="ml-auto p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
